import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Lock, Mail } from "lucide-react";
import { projects } from "../lib/data";

const TiltCard = ({ project, index, reverse }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 15 });
  const ry = useSpring(useTransform(mx, [0, 1], [-7, 7]), { stiffness: 120, damping: 15 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };
  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 80 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-14 items-center"
      data-testid={`project-card-${project.slug}`}
    >
      <div className={`lg:col-span-4 ${reverse ? "lg:order-2 lg:pl-8" : "lg:order-1 lg:pr-8"}`}>
        <div className="flex items-center gap-2 mb-6">
          <span className="chip chip-accent">({project.index})</span>
          <span className="chip">{project.year}</span>
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-3">
          {project.subtitle}
        </div>
        <h3 className="h-display text-5xl md:text-6xl lg:text-7xl tracking-tighter mb-6">
          {project.title}
        </h3>
        <p className="text-[color:var(--nn-text)]/80 text-base md:text-lg leading-relaxed mb-8 max-w-md">
          {project.summary}
        </p>

        <div className="grid grid-cols-2 gap-6 mb-8 max-w-md">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-1">Role</div>
            <div className="text-sm">{project.role}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-1">Timeline</div>
            <div className="text-sm">{project.timeline}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-8 max-w-md">
          {project.tags.map((t) => (
            <span key={t} className="chip">{t}</span>
          ))}
        </div>

        <Link
          to={`/work/${project.slug}`}
          data-cursor="dragging"
          data-cursor-label="open case study"
          data-testid={`project-cta-${project.slug}`}
          className="group inline-flex items-center gap-2 pl-5 pr-2 py-2 rounded-full bg-[color:var(--nn-accent)] text-[color:var(--nn-accent-ink)] font-medium hover:gap-3 transition-all"
        >
          Read case study
          <span className="w-9 h-9 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)] text-[color:var(--nn-accent)] group-hover:rotate-45 transition-transform duration-500">
            <ArrowUpRight size={16} />
          </span>
        </Link>

        {project.nda && (
          <div
            data-testid={`project-nda-note-${project.slug}`}
            className="mt-5 flex items-center gap-2 text-xs md:text-sm text-[color:var(--nn-muted)] max-w-md"
          >
            <Lock size={12} className="shrink-0 text-[color:var(--nn-accent-text)]" />
            <span>
              NDA · Case study available on request —{" "}
              <a
                href={`mailto:niravk.naik@gmail.com?subject=${encodeURIComponent(
                  `Case study access — ${project.title}`
                )}`}
                data-cursor="hover"
                className="text-[color:var(--nn-text)] link-underline inline-flex items-center gap-1"
              >
                <Mail size={11} /> email me
              </a>
            </span>
          </div>
        )}
      </div>

      {/* Visual column — real cover or placeholder */}
      <div className={`lg:col-span-8 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
        <Link
          to={`/work/${project.slug}`}
          data-cursor="dragging"
          data-cursor-label="view project"
          className="block"
        >
          <motion.div
            ref={ref}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
            style={{ rotateX: rx, rotateY: ry, transformPerspective: 1400 }}
            className="relative aspect-[16/10] rounded-3xl overflow-hidden border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/50 group"
          >
            {project.assets?.cover ? (
              <img
                src={project.assets.cover}
                alt={`${project.title} — thumbnail`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1400ms] group-hover:scale-[1.03]"
                loading="lazy"
              />
            ) : (
              <>
                {/* Grid backdrop */}
                <div
                  aria-hidden
                  className="absolute inset-0 opacity-25"
                  style={{
                    backgroundImage:
                      "linear-gradient(to right, var(--nn-border) 1px, transparent 1px), linear-gradient(to bottom, var(--nn-border) 1px, transparent 1px)",
                    backgroundSize: "44px 44px",
                  }}
                />
                {/* Ambient orb */}
                <div
                  aria-hidden
                  className="absolute -bottom-32 -right-32 w-[70%] h-[70%] rounded-full opacity-30 blur-3xl"
                  style={{
                    background:
                      "radial-gradient(closest-side, rgba(223,255,28,0.4), transparent 70%)",
                  }}
                />
                {/* Text-driven "no image" hero */}
                <div className="absolute inset-0 flex flex-col justify-between p-8 md:p-10">
                  <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
                    <span>▚ {project.subtitle}</span>
                    <span>{project.year}</span>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-4">
                      Case study · read below
                    </div>
                    <h4 className="h-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.86] mb-4">
                      {project.title}
                    </h4>
                    <p className="text-[color:var(--nn-text)]/80 text-sm md:text-base max-w-md leading-relaxed">
                      {project.summary}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.map((t) => (
                      <span key={t} className="chip">{t}</span>
                    ))}
                  </div>
                </div>
              </>
            )}
            {/* Corner arrow indicator */}
            <div className="absolute top-4 right-4 w-12 h-12 rounded-full grid place-items-center bg-[color:var(--nn-accent-ink)]/70 backdrop-blur border border-white/25 text-white group-hover:bg-[color:var(--nn-accent)] group-hover:text-[color:var(--nn-accent-ink)] group-hover:border-[color:var(--nn-accent)] group-hover:rotate-45 transition-all duration-500 z-10">
              <ArrowUpRight size={18} />
            </div>
          </motion.div>
        </Link>
      </div>
    </motion.article>
  );
};

const SelectedWork = () => {
  return (
    <section id="work" className="py-24 md:py-40 relative" data-testid="selected-work">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 md:mb-24 items-end">
          <div className="md:col-span-8">
            <div className="section-label mb-5">◆ Selected Work · 002</div>
            <h2 className="h-display text-6xl md:text-8xl lg:text-[8.5rem] tracking-tighter leading-[0.85]">
              Case <span className="font-serif-i text-[color:var(--nn-muted)]">studies</span>
            </h2>
          </div>
          <p className="md:col-span-4 text-[color:var(--nn-text)]/75 text-sm md:text-base leading-relaxed">
            Two flagship products — an EdTech video editor rebuilt around AI + accessibility, and an
            enterprise interview platform designed for defensible hiring decisions.
          </p>
        </div>

        <div className="space-y-28 md:space-y-40">
          {projects.map((p, i) => (
            <TiltCard key={p.slug} project={p} index={i} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SelectedWork;
